import { getMinimalListsAndInspirations } from 'apis/list';
import { MinimalList } from 'apis/list/graphqlTypes';
import { reportError } from 'helpers/sentry';
import { country } from 'utils/settings';
import { appStorage } from './appStorage';

type FavouritesPayload = {
    favouritedItems: string[];
};

type InspirationsPayload = {
    inspirationIds: Record<string, boolean>;
};

type Callback = (data: FavouritesPayload | InspirationsPayload) => void;

export const FAVOURITES_STORAGE_KEY = `favourites-state-${country}`;
export const INSPIRATIONS_STORAGE_KEY = `inspiration-state-${country}`;

const favouritesListeners: Callback[] = [];
const inspirationsListeners: Callback[] = [];

/** Helpers */
export const refreshCache = async () => {
    const response = await getMinimalListsAndInspirations();
    updateMultiListStore(response.data.lists);
    updateInspirationsStore(response.data.inspirations);
};

export const checkCacheValidity = () => {
    const inspirations = getInspirationsFromCache();
    if (inspirations === null) {
        refreshCache();
    }
};

/** subscribe to changes in the stored data */
export const subscribeFavourites = (callback: Callback) => {
    favouritesListeners.push(callback);
    const favourites = getFavouritesFromCache();

    if (favourites) {
        callback({ favouritedItems: favourites });
    }
};

export const subscribeInspirations = (callback: Callback) => {
    inspirationsListeners.push(callback);
    const inspirations = getInspirationsFromCache();

    if (inspirations) {
        callback({ inspirationIds: inspirations.inspirationIds });
    }
};

const getFavouritesFromCache = () => {
    try {
        const stored = appStorage(FAVOURITES_STORAGE_KEY);
        if (stored === null) {
            return null;
        }
        return stored.length ? JSON.parse(stored) : [];
    } catch (e) {
        reportError(e as Error);
    }
    return [];
};

export const getInspirationsFromCache = () => {
    try {
        const stored = appStorage(INSPIRATIONS_STORAGE_KEY);

        if (stored === null) {
            return null;
        }

        return stored.length ? JSON.parse(stored) : { inspirationIds: {} };
    } catch (e) {
        reportError(e as Error);
    }

    return null;
};

export const updateMultiListStore = (lists: MinimalList[]) => {
    const items: string[] = [];
    lists.forEach(list => {
        list.items.forEach(item => items.push(item.itemNo));
    });
    appStorage(FAVOURITES_STORAGE_KEY, JSON.stringify(items));
    favouritesListeners.forEach(cb => cb({ favouritedItems: items }));
};

export const updateInspirationsStore = (inspirations: Array<{ id: string }>) => {
    const inspirationsMap: InspirationsPayload = { inspirationIds: {} };

    inspirations.forEach(inspiration => (inspirationsMap.inspirationIds[inspiration.id] = true));

    appStorage(INSPIRATIONS_STORAGE_KEY, JSON.stringify(inspirationsMap));

    inspirationsListeners.forEach(cb => cb(inspirationsMap));

    return inspirationsMap;
};

export const removeItemFromStore = (itemNo: string) => {
    let itemsStr = appStorage(FAVOURITES_STORAGE_KEY);
    if (itemsStr) {
        const items: Array<string> = JSON.parse(itemsStr);
        const updatedList = items.filter(persistedItemNo => persistedItemNo !== itemNo);
        appStorage(FAVOURITES_STORAGE_KEY, JSON.stringify(updatedList));
        favouritesListeners.forEach(cb => cb({ favouritedItems: updatedList }));
    }
};

export const clearInspirationsStore = () => updateInspirationsStore([]);
export const clearFavouritesStore = () => appStorage(FAVOURITES_STORAGE_KEY, null);

/**
 * Helper function to check if an inspirational image has been favourited or not
 * @param {Array<string>} ids List of IDs to check favourited status
 * @returns {Record<string, boolean>} key/value pair of requested ids and their favourited status
 */
export const isImageFavourited = (ids: Array<string>) => {
    const stored = getInspirationsFromCache();
    const result: Record<string, boolean> = {};

    if (stored) {
        ids.forEach(id => {
            result[id] = Object.prototype.hasOwnProperty.call(stored.inspirationIds, id);
        });
    }

    return result;
};

export const allFavouritedImagesInStorage = () => {
    const stored = getInspirationsFromCache();

    return Object.keys(stored?.inspirationIds ?? {});
};

/** Runs on import */
checkCacheValidity();
